// ============================================================
// EvalBar — Visual advantage indicator
// Vertical on desktop (left of board), horizontal on mobile (above board)
// No numbers — proportional fill only, with sigmoid scaling
// ============================================================

import styles from "./EvalBar.module.css"

interface EvalBarProps {
  /** Centipawn evaluation from White's perspective */
  eval_cp: number
  /** Whether the bar is in a critical moment (dramatic shift) */
  isCritical?: boolean
  /** Orientation: vertical for desktop, horizontal for mobile */
  orientation: "vertical" | "horizontal"
}

/**
 * Sigmoid-like scaling function for eval bar.
 * Maps centipawn eval to a 0-1 range where:
 * - 0 = completely black winning
 * - 0.5 = equal
 * - 1 = completely white winning
 *
 * Uses tanh-based sigmoid so:
 * - Small advantages (< 100cp) barely move the bar
 * - Medium advantages (100-300cp) show moderate shift
 * - Large advantages (> 500cp) show dramatic shift
 * - Mate scores pin to the extremes
 */
function evalToPercentage(eval_cp: number): number {
  // Mate scores
  if (eval_cp >= 9000) return 0.98
  if (eval_cp <= -9000) return 0.02

  // Sigmoid scaling using tanh
  // Divisor controls sensitivity: 400 means +400cp = ~76% white
  const scaled = Math.tanh(eval_cp / 400)

  // Map from [-1, 1] to [0.02, 0.98] (never fully one-sided visually)
  return 0.5 + scaled * 0.48
}

export const EvalBar = ({
  eval_cp,
  isCritical = false,
  orientation,
}: EvalBarProps) => {
  const whitePortion = evalToPercentage(eval_cp)
  const isVertical = orientation === "vertical"

  const containerClass = `${styles.container} ${
    isVertical ? styles.vertical : styles.horizontal
  } ${isCritical ? styles.critical : ""}`

  const whiteStyle = isVertical
    ? { height: `${whitePortion * 100}%` }
    : { width: `${whitePortion * 100}%` }

  const blackStyle = isVertical
    ? { height: `${(1 - whitePortion) * 100}%` }
    : { width: `${(1 - whitePortion) * 100}%` }

  return (
    <div
      className={containerClass}
      role="meter"
      aria-label="Position advantage"
      aria-valuenow={eval_cp}
      aria-valuemin={-9999}
      aria-valuemax={9999}
    >
      {isVertical ? (
        <>
          <div className={styles.blackSection} style={blackStyle} />
          <div className={styles.whiteSection} style={whiteStyle} />
        </>
      ) : (
        <>
          <div className={styles.whiteSection} style={whiteStyle} />
          <div className={styles.blackSection} style={blackStyle} />
        </>
      )}
    </div>
  )
}
